import { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import LexicalEditor, {
  LexicalEditorHandle,
} from '@/components/ui/lexical-editor';
import { Textarea } from '@/components/ui/textarea';
import { useCommentForm } from '@/redux/commet/useCommentForm';
import { UserState } from '@/redux/auth/auth';
import { setShowLoginDialog } from '@/redux/global/global.slice';

/** A Lexical document holding one line of plain text, for the @mention prefill. */
export function plainTextState(text: string): string {
  return JSON.stringify({
    root: {
      children: [
        {
          children: [
            {
              detail: 0,
              format: 0,
              mode: 'normal',
              style: '',
              text,
              type: 'text',
              version: 1,
            },
          ],
          direction: 'ltr',
          format: '',
          indent: 0,
          type: 'paragraph',
          version: 1,
        },
      ],
      direction: 'ltr',
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
    },
  });
}

const VARIANT = {
  full: {
    minHeight: 'min-h-[96px]',
    submitClass: 'rounded-md w-full md:w-auto',
    size: 'default' as const,
  },
  compact: {
    minHeight: 'min-h-[64px]',
    submitClass: 'rounded-md',
    size: 'sm' as const,
  },
};

type ComposerVariant = keyof typeof VARIANT;

function ComposerActions({
  variant,
  onCancel,
  submitLabel,
  isAuthenticated,
}: {
  variant: ComposerVariant;
  onCancel?: () => void;
  submitLabel: string;
  isAuthenticated: boolean;
}) {
  const { size, submitClass } = VARIANT[variant];

  return (
    <div className="flex justify-end gap-2 mt-1">
      {onCancel && (
        <Button type="button" variant="ghost" size={size} onClick={onCancel}>
          Cancel
        </Button>
      )}
      <Button
        type="submit"
        variant="default"
        size={size}
        className={submitClass}
      >
        {isAuthenticated ? submitLabel : `Login to ${submitLabel}`}
      </Button>
    </div>
  );
}

/**
 * The comment editor, shared by the post form, the project-group form and the
 * inline reply box. Signed out, submitting opens the login dialog instead.
 */
export default function CommentComposer({
  onSubmit,
  onCancel,
  initialState,
  placeholder = 'Write your comment',
  submitLabel = 'Comment',
  variant = 'full',
}: {
  onSubmit: (serializedState: string) => Promise<void>;
  onCancel?: () => void;
  initialState?: string;
  placeholder?: string;
  submitLabel?: string;
  variant?: ComposerVariant;
}) {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector(
    (state: { auth: UserState }) => state.auth
  );
  const editorRef = useRef<LexicalEditorHandle | null>(null);
  const commentForm = useCommentForm();

  async function handleSubmit() {
    if (!isAuthenticated) {
      dispatch(setShowLoginDialog(true));
      return;
    }

    const body = editorRef.current?.getSerializedState();
    if (!body) return;

    await onSubmit(body);
    editorRef.current?.clear();
    commentForm.reset();
  }

  return (
    <Form {...commentForm}>
      <form
        onSubmit={commentForm.handleSubmit(handleSubmit)}
        className="flex flex-col gap-2"
      >
        <FormField
          control={commentForm.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder="Enter your comments"
                  {...field}
                  className="hidden"
                />
              </FormControl>
              <div className="flex flex-col gap-2">
                <LexicalEditor
                  ref={editorRef}
                  initialState={initialState}
                  placeholder={placeholder}
                  onChange={(state) => field.onChange(state)}
                  className="border border-border rounded-md bg-card"
                  contentClassName={VARIANT[variant].minHeight}
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <ComposerActions
          variant={variant}
          onCancel={onCancel}
          submitLabel={submitLabel}
          isAuthenticated={isAuthenticated}
        />
      </form>
    </Form>
  );
}
